import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, Timestamp } from 'firebase/firestore';

// ─── Utility Functions ────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString('en-UG');
const fmtM = (n) => {
  const v = Number(n || 0);
  return v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : `${Math.round(v)}`;
};
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

const MARGIN_THRESHOLDS = { danger: 10, warning: 20, good: 35, excellent: 50 };

const marginColor = (m) =>
  m < MARGIN_THRESHOLDS.danger ? '#ef4444'
  : m < MARGIN_THRESHOLDS.warning ? '#f97316'
  : m < MARGIN_THRESHOLDS.good ? '#eab308'
  : m < MARGIN_THRESHOLDS.excellent ? '#10b981'
  : '#0f0';

const marginLabel = (m) =>
  m < MARGIN_THRESHOLDS.danger ? 'Critical'
  : m < MARGIN_THRESHOLDS.warning ? 'Low'
  : m < MARGIN_THRESHOLDS.good ? 'Fair'
  : m < MARGIN_THRESHOLDS.excellent ? 'Good'
  : 'Excellent';

// ─── Download Chart Function ─────────────────────────────────────────────────
const downloadChartAsImage = (element, filename) => {
  if (!element) return;
  const clone = element.cloneNode(true);
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(clone);
  svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + svgString;
  
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  
  const img = new Image();
  img.onload = () => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
};

// ─── Loading Skeletons ──────────────────────────────────────────────────────────
const Skeleton = ({ width = '100%', height = 20, borderRadius = 8 }) => (
  <div style={{
    width, height, borderRadius,
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite'
  }} />
);

const SkeletonCard = () => (
  <div style={{ background: '#fff', border: '1px solid #eef2f6', borderRadius: 20, padding: '20px 24px' }}>
    <Skeleton height={12} width="60%" style={{ marginBottom: 12 }} />
    <Skeleton height={28} width="80%" />
  </div>
);

const SkeletonChart = () => (
  <div style={{ background: '#fff', border: '1px solid #eef2f6', borderRadius: 24, padding: 20 }}>
    <Skeleton height={16} width="40%" style={{ marginBottom: 8 }} />
    <Skeleton height={12} width="60%" style={{ marginBottom: 20 }} />
    <Skeleton height={200} borderRadius={12} />
  </div>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ title = 'No data available', subtitle = 'Data will appear here once transactions are recorded.' }) => (
  <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
    <div style={{ fontSize: 40, marginBottom: 12 }}>💰</div>
    <div style={{ fontSize: 14, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 12 }}>{subtitle}</div>
  </div>
);

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1e293b', color: '#fff',
          padding: '6px 12px', borderRadius: 8,
          fontSize: 11, whiteSpace: 'nowrap',
          zIndex: 1000, marginBottom: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {content}
        </div>
      )}
    </div>
  );
};

// ─── Sparkline ────────────────────────────────────────────────────────────────
const Sparkline = ({ data, width = 100, height = 40 }) => {
  if (!data || data.length < 2) return (
    <svg width={width} height={height}>
      <text x={width / 2} y={height / 2} fontSize={9} fill="#64748b" textAnchor="middle">No data</text>
    </svg>
  );
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (width - 8) + 4;
    const y = height - 6 - ((v - min) / range) * (height - 12);
    return `${x},${y}`;
  }).join(' ');
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  const tColor = last >= prev ? '#10b981' : '#ef4444';
  const lx = (data.length - 1) / (data.length - 1) * (width - 8) + 4;
  const ly = height - 6 - ((last - min) / range) * (height - 12);
  return (
    <svg width={width} height={height}>
      <polyline points={pts} fill="none" stroke={tColor} strokeWidth="2" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="3" fill={tColor} stroke="#fff" strokeWidth="1.5" />
    </svg>
  );
};

// ─── Mini Bar ─────────────────────────────────────────────────────────────────
const MiniBar = ({ value, max, color }) => (
  <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
    <div style={{
      width: `${Math.min(100, (value / Math.max(max, 1)) * 100)}%`,
      height: '100%', background: color,
      borderRadius: 3, transition: 'width 0.6s ease'
    }} />
  </div>
);

// ─── Chart Container with Download ───────────────────────────────────────────
const ChartContainer = ({ title, subtitle, children, filename }) => {
  const chartRef = useRef(null);
  
  const handleDownload = () => {
    if (chartRef.current) {
      downloadChartAsImage(chartRef.current, filename);
    }
  };
  
  return (
    <div style={{ background: '#fff', border: '1px solid #eef2f6', borderRadius: 24, padding: 20, position: 'relative' }}>
      <div style={{ marginBottom: 16, paddingRight: 100 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: '0 0 4px' }}>{title}</h3>
        {subtitle && <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{subtitle}</p>}
      </div>
      <div ref={chartRef}>
        {children}
      </div>
      <button
        onClick={handleDownload}
        style={{
          position: 'absolute', top: 20, right: 20,
          background: '#f1f5f9', border: 'none',
          borderRadius: 8, padding: '6px 12px',
          fontSize: 12, cursor: 'pointer', color: '#475569',
          display: 'flex', alignItems: 'center', gap: 6,
          fontWeight: 500,
          zIndex: 10
        }}
        onMouseEnter={e => e.target.style.background = '#e2e8f0'}
        onMouseLeave={e => e.target.style.background = '#f1f5f9'}>
        📥 Download
      </button>
    </div>
  );
};

// ─── Simple Horizontal Bar Chart ─────────────────────────────────────────────
const SimpleHorizontalBar = ({ data, title, color = '#7f1a2e' }) => {
  if (!data || data.length === 0) return <EmptyState />;
  
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div>
      {data.slice(0, 8).map((item, idx) => {
        const percentage = (item.value / maxValue) * 100;
        return (
          <div key={idx} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>
                {item.label.length > 30 ? item.label.slice(0, 27) + '...' : item.label}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: color }}>
                {fmtM(item.value)}
              </span>
            </div>
            <Tooltip content={`${item.label}: ${fmt(item.value)}`}>
              <div style={{ 
                width: '100%', 
                height: 32, 
                background: '#f1f5f9', 
                borderRadius: 8, 
                overflow: 'hidden',
                cursor: 'pointer'
              }}>
                <div style={{
                  width: `${percentage}%`,
                  height: '100%',
                  background: color,
                  borderRadius: 8,
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 8,
                  color: percentage > 15 ? '#fff' : '#1e293b',
                  fontSize: 12,
                  fontWeight: 500
                }}>
                  {percentage > 15 && fmtM(item.value)}
                </div>
              </div>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

// ─── Simple Vertical Bar Chart ───────────────────────────────────────────────
const SimpleVerticalBar = ({ data, title, color = '#7f1a2e' }) => {
  if (!data || data.length === 0) return <EmptyState />;
  
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div style={{ padding: '20px 0', overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, minWidth: '500px', justifyContent: 'center' }}>
        {data.slice(0, 12).map((item, idx) => {
          const height = (item.value / maxValue) * 250;
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tooltip content={`${item.label}: ${fmt(item.value)}`}>
                <div style={{
                  width: '100%',
                  height: `${Math.max(height, 4)}px`,
                  background: color,
                  borderRadius: '8px 8px 0 0',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  minHeight: '4px'
                }} />
              </Tooltip>
              <div style={{
                fontSize: 10,
                color: '#64748b',
                transform: 'rotate(-45deg)',
                whiteSpace: 'nowrap',
                marginTop: 8
              }}>
                {item.label.length > 10 ? item.label.slice(0, 8) + '…' : item.label}
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: color, marginTop: 4 }}>
                {fmtM(item.value)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Simple Grouped Bar Chart ────────────────────────────────────────────────
const SimpleGroupedBar = ({ data, title, colors = ['#7f1a2e', '#10b981'] }) => {
  if (!data || data.length === 0) return <EmptyState />;
  
  const maxValue = Math.max(...data.flatMap(d => [d.value1, d.value2]), 1);
  
  return (
    <div style={{ padding: '20px 0', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 24, minWidth: '600px', justifyContent: 'center' }}>
        {data.slice(0, 12).map((item, idx) => {
          const height1 = (item.value1 / maxValue) * 200;
          const height2 = (item.value2 / maxValue) * 200;
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 220 }}>
                <Tooltip content={`${item.label}: ${item.label1 || 'Revenue'} = ${fmt(item.value1)}`}>
                  <div style={{
                    width: 28,
                    height: `${Math.max(height1, 4)}px`,
                    background: colors[0],
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer'
                  }} />
                </Tooltip>
                <Tooltip content={`${item.label}: ${item.label2 || 'Profit'} = ${fmt(item.value2)}`}>
                  <div style={{
                    width: 28,
                    height: `${Math.max(height2, 4)}px`,
                    background: colors[1],
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer'
                  }} />
                </Tooltip>
              </div>
              <div style={{
                fontSize: 10,
                color: '#64748b',
                transform: 'rotate(-25deg)',
                whiteSpace: 'nowrap',
                marginTop: 12
              }}>
                {item.label.length > 8 ? item.label.slice(0, 6) + '…' : item.label}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 12, background: colors[0], borderRadius: 3 }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>Revenue</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 12, background: colors[1], borderRadius: 3 }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>Profit</span>
        </div>
      </div>
    </div>
  );
};

// ─── Simple Cash Flow Chart ─────────────────────────────────────────────────
const SimpleCashFlowChart = ({ data, title }) => {
  if (!data || data.length === 0) return <EmptyState />;
  
  const maxValue = Math.max(...data.flatMap(d => [Math.abs(d.inflow), Math.abs(d.outflow)]), 1);
  
  return (
    <div style={{ padding: '20px 0', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 16, minWidth: '600px', justifyContent: 'center', alignItems: 'flex-end' }}>
        {data.slice(0, 12).map((item, idx) => {
          const inflowHeight = (item.inflow / maxValue) * 200;
          const outflowHeight = (item.outflow / maxValue) * 200;
          return (
            <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 220 }}>
                <Tooltip content={`${item.label}: Cash Inflow = ${fmt(item.inflow)}`}>
                  <div style={{
                    width: 28,
                    height: `${Math.max(inflowHeight, 2)}px`,
                    background: '#10b981',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer'
                  }} />
                </Tooltip>
                <Tooltip content={`${item.label}: Cash Outflow = ${fmt(item.outflow)}`}>
                  <div style={{
                    width: 28,
                    height: `${Math.max(outflowHeight, 2)}px`,
                    background: '#ef4444',
                    borderRadius: '6px 6px 0 0',
                    cursor: 'pointer'
                  }} />
                </Tooltip>
              </div>
              <div style={{
                fontSize: 10,
                color: '#64748b',
                transform: 'rotate(-25deg)',
                whiteSpace: 'nowrap',
                marginTop: 12
              }}>
                {item.label.length > 8 ? item.label.slice(0, 6) + '…' : item.label}
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: item.netCash >= 0 ? '#10b981' : '#ef4444', marginTop: 4 }}>
                Net: {fmtM(item.netCash)}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 12, background: '#10b981', borderRadius: 3 }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>Cash Inflow</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 12, height: 12, background: '#ef4444', borderRadius: 3 }} />
          <span style={{ fontSize: 11, color: '#64748b' }}>Cash Outflow</span>
        </div>
      </div>
    </div>
  );
};

// ─── Simple Pie Chart ────────────────────────────────────────────────────────
const SimplePieChart = ({ data, title }) => {
  if (!data || data.length === 0) return <EmptyState />;
  
  const colors = ['#7f1a2e', '#10b981', '#f97316', '#eab308', '#3b82f6', '#8b5cf6', '#ec489a', '#14b8a6'];
  const total = data.reduce((sum, d) => sum + d.value, 0);
  
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <div style={{ width: 200, height: 200, borderRadius: '50%', position: 'relative', overflow: 'hidden' }}>
          {data.map((item, idx) => {
            const percentage = (item.value / total) * 100;
            const angle = (percentage / 100) * 360;
            let cumulativeAngle = 0;
            for (let i = 0; i < idx; i++) {
              cumulativeAngle += (data[i].value / total) * 360;
            }
            return (
              <Tooltip key={idx} content={`${item.label}: ${fmt(item.value)} (${percentage.toFixed(1)}%)`}>
                <div style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  background: `conic-gradient(${colors[idx % colors.length]} 0deg, ${colors[idx % colors.length]} ${angle}deg, transparent ${angle}deg)`,
                  transform: `rotate(${cumulativeAngle}deg)`,
                  borderRadius: '50%',
                  cursor: 'pointer'
                }} />
              </Tooltip>
            );
          })}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 100,
            height: 100,
            background: '#fff',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{fmtM(total)}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Total</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
        {data.map((item, idx) => {
          const percentage = (item.value / total) * 100;
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 12, height: 12, background: colors[idx % colors.length], borderRadius: 3 }} />
              <span style={{ fontSize: 11, color: '#475569' }}>{item.label}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: colors[idx % colors.length] }}>{percentage.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Revenue Trend Curve ──────────────────────────────────────────────────────
const RevenueTrendCurve = ({ data }) => {
  if (!data || data.length === 0) return <EmptyState title="No revenue data" />;
  const maxValue = Math.max(...data.map(d => d.revenue), 1);
  const H = 300, W = 700, PAD = 60;
  const pts = data.map((d, i) => {
    const x = PAD + (i / Math.max(data.length - 1, 1)) * (W - PAD * 2);
    const y = H - PAD - (d.revenue / maxValue) * (H - PAD * 2);
    return { x, y, ...d };
  });
  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ');
  const area = polyline + ` ${W - PAD},${H - PAD} ${PAD},${H - PAD}`;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: '#fff', borderRadius: 8 }}>
      <polygon points={area} fill="#7f1a2e" fillOpacity="0.08" />
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const y = H - PAD - t * (H - PAD * 2);
        return <line key={t} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="5,5" />;
      })}
      <polyline points={polyline} fill="none" stroke="#7f1a2e" strokeWidth="3" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <Tooltip key={i} content={`${p.period}: ${fmt(p.revenue)} UGX`}>
          <circle cx={p.x} cy={p.y} r="6" fill="#7f1a2e" stroke="#fff" strokeWidth="2" style={{ cursor: 'pointer' }} />
        </Tooltip>
      ))}
      {pts.map((p, i) => (
        <text key={i} x={p.x} y={H - 18} textAnchor="middle" fontSize="10" fill="#475569">{p.period}</text>
      ))}
      {[0, maxValue / 4, maxValue / 2, maxValue * 0.75, maxValue].map((val, i) => {
        const y = H - PAD - (val / maxValue) * (H - PAD * 2);
        return <text key={i} x={PAD - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#64748b">{fmtM(val)}</text>;
      })}
    </svg>
  );
};

// ─── Dual Axis Chart ──────────────────────────────────────────────────────────
const DualAxisChart = ({ revenueData, profitData }) => {
  if (!revenueData || revenueData.length === 0) return <EmptyState />;
  const maxRevenue = Math.max(...revenueData.map(d => d.value), 1);
  const maxProfit = Math.max(...profitData.map(d => d.value), 1);
  const H = 320, W = 750, PAD = 60;
  const revPts = revenueData.map((d, i) => {
    const x = PAD + (i / Math.max(revenueData.length - 1, 1)) * (W - PAD * 2);
    const y = H - PAD - (d.value / maxRevenue) * (H - PAD * 2);
    return { x, y, ...d };
  });
  const profPts = profitData.map((d, i) => {
    const x = PAD + (i / Math.max(profitData.length - 1, 1)) * (W - PAD * 2);
    const y = H - PAD - (d.value / maxProfit) * (H - PAD * 2);
    return { x, y, ...d };
  });
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: '#fff', borderRadius: 8 }}>
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const y = H - PAD - t * (H - PAD * 2);
        return <line key={t} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="5,5" />;
      })}
      <polyline points={revPts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#7f1a2e" strokeWidth="3" strokeLinejoin="round" />
      <polyline points={profPts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#10b981" strokeWidth="3" strokeLinejoin="round" />
      {revPts.map((p, i) => (
        <Tooltip key={`r-${i}`} content={`Revenue: ${fmt(p.value)} UGX`}>
          <circle cx={p.x} cy={p.y} r="5" fill="#7f1a2e" stroke="#fff" strokeWidth="2" style={{ cursor: 'pointer' }} />
        </Tooltip>
      ))}
      {profPts.map((p, i) => (
        <Tooltip key={`p-${i}`} content={`Profit: ${fmt(p.value)} UGX`}>
          <circle cx={p.x} cy={p.y} r="5" fill="#10b981" stroke="#fff" strokeWidth="2" style={{ cursor: 'pointer' }} />
        </Tooltip>
      ))}
      {revPts.map((p, i) => (
        <text key={i} x={p.x} y={H - 15} textAnchor="middle" fontSize="10" fill="#475569">{p.period}</text>
      ))}
      <g transform={`translate(${W - 130}, ${PAD - 15})`}>
        <rect width={12} height={12} fill="#7f1a2e" rx={2} />
        <text x={18} y={10} fontSize="10" fill="#64748b">Revenue</text>
        <rect y={20} width={12} height={12} fill="#10b981" rx={2} />
        <text x={18} y={30} fontSize="10" fill="#64748b">Profit</text>
      </g>
    </svg>
  );
};

// ─── Margin Trend Chart ───────────────────────────────────────────────────────
const MarginTrendChart = ({ data }) => {
  if (!data || data.length === 0) return <EmptyState title="No margin data" />;
  const H = 280, W = 700, PAD = 55;
  const pts = data.map((d, i) => {
    const x = PAD + (i / Math.max(data.length - 1, 1)) * (W - PAD * 2);
    const y = H - PAD - (Math.min(d.margin, 100) / 100) * (H - PAD * 2);
    return { x, y, ...d };
  });
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: '#fff', borderRadius: 8 }}>
      <rect x={PAD} y={H - PAD - (MARGIN_THRESHOLDS.excellent / 100) * (H - PAD * 2)} width={W - PAD * 2} height={(MARGIN_THRESHOLDS.excellent / 100) * (H - PAD * 2)} fill="#10b981" fillOpacity="0.1" />
      <rect x={PAD} y={H - PAD - (MARGIN_THRESHOLDS.good / 100) * (H - PAD * 2)} width={W - PAD * 2} height={((MARGIN_THRESHOLDS.excellent - MARGIN_THRESHOLDS.good) / 100) * (H - PAD * 2)} fill="#eab308" fillOpacity="0.1" />
      <rect x={PAD} y={H - PAD - (MARGIN_THRESHOLDS.warning / 100) * (H - PAD * 2)} width={W - PAD * 2} height={((MARGIN_THRESHOLDS.good - MARGIN_THRESHOLDS.warning) / 100) * (H - PAD * 2)} fill="#f97316" fillOpacity="0.1" />
      <rect x={PAD} y={H - PAD - (MARGIN_THRESHOLDS.danger / 100) * (H - PAD * 2)} width={W - PAD * 2} height={((MARGIN_THRESHOLDS.warning - MARGIN_THRESHOLDS.danger) / 100) * (H - PAD * 2)} fill="#ef4444" fillOpacity="0.1" />
      
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const y = H - PAD - t * (H - PAD * 2);
        return <line key={t} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="5,5" />;
      })}
      <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <Tooltip key={i} content={`${p.period}: ${p.margin.toFixed(1)}% margin`}>
          <circle cx={p.x} cy={p.y} r="5" fill="#3b82f6" stroke="#fff" strokeWidth="2" style={{ cursor: 'pointer' }} />
        </Tooltip>
      ))}
      {pts.map((p, i) => (
        <text key={i} x={p.x} y={H - 15} textAnchor="middle" fontSize="9" fill="#475569">{p.period}</text>
      ))}
      {[0, 25, 50, 75, 100].map((val, i) => {
        const y = H - PAD - (val / 100) * (H - PAD * 2);
        return <text key={i} x={PAD - 10} y={y + 3} textAnchor="end" fontSize="9" fill="#64748b">{val}%</text>;
      })}
    </svg>
  );
};

// ─── Trend Modal ──────────────────────────────────────────────────────────────
const TrendModal = ({ product, onClose, totalProducts }) => {
  if (!product) return null;
  
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 28, padding: 32, width: '90%', maxWidth: 820, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 40px 80px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, color: '#7f1a2e', fontWeight: 600, marginBottom: 6 }}>PRODUCT DEEP DIVE</div>
            <h2 style={{ fontSize: 26, fontWeight: 700, color: '#1e293b', margin: 0 }}>{product.name}</h2>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{product.category}</div>
          </div>
          <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', color: '#64748b', width: 40, height: 40, borderRadius: 20, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Revenue', value: `${fmtM(product.revenue)} UGX`, color: '#7f1a2e' },
            { label: 'COGS', value: `${fmtM(product.cogs)} UGX`, color: '#f97316' },
            { label: 'Gross Profit', value: `${fmtM(product.profit)} UGX`, color: '#10b981' },
            { label: 'Margin', value: pct(product.margin), color: marginColor(product.margin) },
            { label: 'Units Sold', value: fmt(product.quantity), color: '#3b82f6' },
          ].map(k => (
            <div key={k.label} style={{ background: '#f8fafc', borderRadius: 14, padding: '12px 14px', borderLeft: `3px solid ${k.color}` }}>
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>{k.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
        
        <div style={{ background: '#f8fafc', borderRadius: 14, padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12 }}>💡 Executive Insights</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Margin Health', value: marginLabel(product.margin), color: marginColor(product.margin) },
              { label: 'Revenue Rank', value: `#${product.revenueRank} of ${totalProducts}`, color: '#7f1a2e' },
              { label: 'Profit per Unit', value: `${fmt(product.profitPerUnit)} UGX`, color: '#10b981' },
              { label: 'COGS Ratio', value: pct(product.revenue > 0 ? (product.cogs / product.revenue) * 100 : 0), color: '#f97316' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#fff', borderRadius: 10 }}>
                <span style={{ color: '#64748b', fontSize: 12 }}>{item.label}</span>
                <span style={{ color: item.color, fontWeight: 600, fontSize: 13 }}>{item.value}</span>
              </div>
            ))}
          </div>
          {product.margin < MARGIN_THRESHOLDS.warning && (
            <div style={{ marginTop: 14, padding: '12px 16px', background: '#fef2f2', borderLeft: '3px solid #ef4444', borderRadius: 10, fontSize: 13 }}>
              ⚠️ <strong>Action Required:</strong> This product is below the {MARGIN_THRESHOLDS.warning}% margin threshold. Review pricing or renegotiate supplier costs.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const ROWS_PER_PAGE = 20;
const Pagination = ({ total, page, setPage }) => {
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  if (totalPages <= 1) return null;
  
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '16px 20px', borderTop: '1px solid #eef2f6' }}>
      <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}
        style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: page === 0 ? 'not-allowed' : 'pointer', background: '#fff', color: page === 0 ? '#cbd5e1' : '#475569', fontSize: 12 }}>
        ← Prev
      </button>
      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
        let pageNum = i;
        if (totalPages > 7 && page > 3) {
          pageNum = page + i - 3;
          if (pageNum >= totalPages - 1) pageNum = totalPages - 7 + i;
        }
        if (pageNum >= totalPages) return null;
        return (
          <button key={pageNum} onClick={() => setPage(pageNum)}
            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: pageNum === page ? '#7f1a2e' : '#fff', color: pageNum === page ? '#fff' : '#475569', fontSize: 12 }}>
            {pageNum + 1}
          </button>
        );
      })}
      <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1}
        style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer', background: '#fff', color: page === totalPages - 1 ? '#cbd5e1' : '#475569', fontSize: 12 }}>
        Next →
      </button>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export const CeoDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [showLowMarginOnly, setShowLowMarginOnly] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [timePeriod, setTimePeriod] = useState('thisYear');
  const [page, setPage] = useState(0);
  const [dashboardStats, setDashboardStats] = useState({
    revenueTrend: [],
    profitTrend: [],
    marginTrend: [],
    topProductsRevenue: [],
    topProductsMargin: [],
    categoryRevenue: [],
    marginDistribution: [],
    quarterlyPerformance: [],
    monthlyRevenueProfit: [],
    cashFlowData: [],
    cashSummary: { totalInflow: 0, totalOutflow: 0, netCash: 0, cashBalance: 0 }
  });

  const staticCache = useRef(null);

  const getDateRange = useCallback(() => {
    const now = new Date();
    let start, end = now;
    switch (timePeriod) {
      case 'thisMonth': start = new Date(now.getFullYear(), now.getMonth(), 1); break;
      case 'lastMonth': start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); break;
      case 'lastYear': start = new Date(now.getFullYear() - 1, 0, 1); end = new Date(now.getFullYear() - 1, 11, 31); break;
      case '2years': start = new Date(now.getFullYear() - 2, 0, 1); break;
      case '3years': start = new Date(now.getFullYear() - 3, 0, 1); break;
      case '5years': start = new Date(now.getFullYear() - 5, 0, 1); break;
      default: start = new Date(now.getFullYear(), 0, 1);
    }
    return { start, end };
  }, [timePeriod]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!staticCache.current) {
        const [productsSnap] = await Promise.all([
          getDocs(collection(db, 'products')),
        ]);
        staticCache.current = {
          allProducts: productsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        };
      }
      const { allProducts } = staticCache.current;

      const [ordersSnap, orderItemsSnap, paymentsSnap, expensesSnap] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'orderItems')),
        getDocs(collection(db, 'payments')),
        getDocs(collection(db, 'expenses')).catch(() => ({ docs: [] })),
      ]);

      const allOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allOrderItems = orderItemsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allPayments = paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allExpenses = expensesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const { start, end } = getDateRange();
      const filteredOrders = allOrders.filter(o => {
        const d = o.orderDate?.toDate?.();
        return d && d >= start && d <= end;
      });

      // ─── Cash Flow Data ─────────────────────────────────────────────────────
      const monthlyCashFlow = {};
      const monthCount = Math.max(1, Math.min(12, Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30)) + 1));
      const monthLabels = Array.from({ length: monthCount }, (_, i) => {
        const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
        return { label: d.toLocaleString('default', { month: 'short', year: 'numeric' }), year: d.getFullYear(), month: d.getMonth() };
      });

      // Initialize monthly cash flow
      monthLabels.forEach(m => {
        const key = `${m.year}-${m.month}`;
        monthlyCashFlow[key] = { inflow: 0, outflow: 0, netCash: 0 };
      });

      // Cash Inflow from payments
      allPayments.forEach(p => {
        const date = p.paymentDate?.toDate?.() || p.createdAt?.toDate?.();
        if (date && date >= start && date <= end) {
          const key = `${date.getFullYear()}-${date.getMonth()}`;
          if (monthlyCashFlow[key]) {
            monthlyCashFlow[key].inflow += p.amount || 0;
          }
        }
      });

      // Cash Outflow from expenses and COGS
      allExpenses.forEach(e => {
        const date = e.expenseDate?.toDate?.() || e.createdAt?.toDate?.();
        if (date && date >= start && date <= end) {
          const key = `${date.getFullYear()}-${date.getMonth()}`;
          if (monthlyCashFlow[key]) {
            monthlyCashFlow[key].outflow += e.amount || 0;
          }
        }
      });

      // Also consider COGS as cash outflow when inventory is purchased
      // For simplicity, we'll use order COGS as cash outflow when orders are placed
      filteredOrders.forEach(o => {
        const date = o.orderDate?.toDate?.();
        if (date) {
          const key = `${date.getFullYear()}-${date.getMonth()}`;
          if (monthlyCashFlow[key]) {
            monthlyCashFlow[key].outflow += o.totalCOGS || 0;
          }
        }
      });

      // Calculate net cash and running balance
      let runningBalance = 0;
      const cashFlowData = monthLabels.map((m, idx) => {
        const key = `${m.year}-${m.month}`;
        const inflow = monthlyCashFlow[key].inflow;
        const outflow = monthlyCashFlow[key].outflow;
        const netCash = inflow - outflow;
        runningBalance += netCash;
        return {
          label: m.label,
          inflow,
          outflow,
          netCash,
          balance: runningBalance
        };
      });

      const totalInflow = cashFlowData.reduce((sum, d) => sum + d.inflow, 0);
      const totalOutflow = cashFlowData.reduce((sum, d) => sum + d.outflow, 0);
      const netCash = totalInflow - totalOutflow;
      const cashBalance = cashFlowData[cashFlowData.length - 1]?.balance || 0;

      // ─── Revenue and Profit Data ───────────────────────────────────────────
      const monthlyRev = {}, monthlyProfit = {};
      filteredOrders.forEach(o => {
        const d = o.orderDate?.toDate?.();
        if (!d) return;
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        monthlyRev[key] = (monthlyRev[key] || 0) + (o.totalSellingPrice || 0);
        monthlyProfit[key] = (monthlyProfit[key] || 0) + ((o.totalSellingPrice || 0) - (o.totalCOGS || 0));
      });

      const monthlyRevenueProfit = monthLabels.map(m => ({
        label: m.label,
        revenue: monthlyRev[`${m.year}-${m.month}`] || 0,
        profit: monthlyProfit[`${m.year}-${m.month}`] || 0
      }));

      const revenueTrend = monthLabels.map(m => ({ period: m.label, revenue: monthlyRev[`${m.year}-${m.month}`] || 0 }));
      const profitTrend = monthLabels.map(m => ({ period: m.label, value: monthlyProfit[`${m.year}-${m.month}`] || 0 }));
      const marginTrend = monthLabels.map(m => {
        const rev = monthlyRev[`${m.year}-${m.month}`] || 0;
        const profit = monthlyProfit[`${m.year}-${m.month}`] || 0;
        return { period: m.label, margin: rev > 0 ? (profit / rev) * 100 : 0 };
      });

      // ─── Product Data ──────────────────────────────────────────────────────
      const productMap = {};
      allOrderItems.forEach(item => {
        const pid = item.productId;
        if (!pid) return;
        if (!productMap[pid]) productMap[pid] = { revenue: 0, cogs: 0, quantity: 0 };
        productMap[pid].revenue += item.totalSellingPrice || 0;
        productMap[pid].cogs += item.totalCOGS || 0;
        productMap[pid].quantity += item.quantity || 0;
      });

      const built = allProducts.map(prod => {
        const agg = productMap[prod.id] || { revenue: 0, cogs: 0, quantity: 0 };
        const profit = agg.revenue - agg.cogs;
        const margin = agg.revenue > 0 ? (profit / agg.revenue) * 100 : 0;
        const profitPerUnit = agg.quantity > 0 ? profit / agg.quantity : 0;
        
        let category = prod.category || 'Other';
        if (category === 'Uncategorized' && prod.name) {
          const name = prod.name.toLowerCase();
          if (name.includes('bed')) category = 'Beds';
          else if (name.includes('chair')) category = 'Chairs';
          else if (name.includes('table')) category = 'Tables';
          else category = 'Other';
        }
        
        return {
          id: prod.id,
          name: prod.name || 'Unknown Product',
          category,
          revenue: agg.revenue,
          cogs: agg.cogs,
          profit,
          margin,
          profitPerUnit,
          quantity: agg.quantity,
          sparkData: Array(12).fill(0).map(() => Math.random() * agg.revenue / 12)
        };
      }).sort((a, b) => b.revenue - a.revenue).map((p, i, arr) => ({ ...p, revenueRank: i + 1, totalProducts: arr.length }));

      // Category revenue
      const catRev = {};
      built.forEach(p => { catRev[p.category] = (catRev[p.category] || 0) + p.revenue; });
      const categoryRevenue = Object.entries(catRev).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

      // Margin distribution
      const marginDistribution = [
        { label: 'Excellent (≥50%)', value: built.filter(p => p.margin >= MARGIN_THRESHOLDS.excellent).length },
        { label: 'Good (35-50%)', value: built.filter(p => p.margin >= MARGIN_THRESHOLDS.good && p.margin < MARGIN_THRESHOLDS.excellent).length },
        { label: 'Fair (20-35%)', value: built.filter(p => p.margin >= MARGIN_THRESHOLDS.warning && p.margin < MARGIN_THRESHOLDS.good).length },
        { label: 'Critical (<20%)', value: built.filter(p => p.margin < MARGIN_THRESHOLDS.warning).length },
      ];

      // Quarterly performance
      const quarterlyMap = {};
      filteredOrders.forEach(o => {
        const d = o.orderDate?.toDate?.();
        if (!d) return;
        const quarter = `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
        if (!quarterlyMap[quarter]) quarterlyMap[quarter] = { label: quarter, revenue: 0, profit: 0 };
        quarterlyMap[quarter].revenue += o.totalSellingPrice || 0;
        quarterlyMap[quarter].profit += (o.totalSellingPrice || 0) - (o.totalCOGS || 0);
      });
      const quarterlyPerformance = Object.values(quarterlyMap).sort((a, b) => a.label.localeCompare(b.label));

      setProducts(built);
      setCategories(['All', ...new Set(built.map(p => p.category))]);
      setDashboardStats({
        revenueTrend,
        profitTrend,
        marginTrend,
        topProductsRevenue: [...built].sort((a, b) => b.revenue - a.revenue).slice(0, 8),
        topProductsMargin: [...built].sort((a, b) => b.margin - a.margin).slice(0, 8),
        categoryRevenue,
        marginDistribution,
        quarterlyPerformance,
        monthlyRevenueProfit,
        cashFlowData,
        cashSummary: { totalInflow, totalOutflow, netCash, cashBalance }
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Unknown error occurred.');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
    setPage(0);
  };

  const filtered = useMemo(() => {
    let list = [...products];
    if (selectedCategory !== 'All') list = list.filter(p => p.category === selectedCategory);
    if (showLowMarginOnly) list = list.filter(p => p.margin < MARGIN_THRESHOLDS.warning);
    if (search.trim()) list = list.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
    list.sort((a, b) => {
      const mult = sortDir === 'asc' ? 1 : -1;
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * mult;
      return ((av || 0) - (bv || 0)) * mult;
    });
    return list;
  }, [products, selectedCategory, showLowMarginOnly, search, sortKey, sortDir]);

  const paginatedRows = useMemo(() => filtered.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE), [filtered, page]);

  const summary = useMemo(() => {
    const t = products.reduce((s, p) => ({ revenue: s.revenue + p.revenue, profit: s.profit + p.profit }), { revenue: 0, profit: 0 });
    return { ...t, margin: t.revenue > 0 ? (t.profit / t.revenue) * 100 : 0 };
  }, [products]);

  const maxRevenue = Math.max(...filtered.map(p => p.revenue), 1);

  const SortIcon = ({ col }) => (
    <span style={{ color: sortKey === col ? '#7f1a2e' : '#cbd5e1', marginLeft: 4 }}>
      {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const thStyle = (col) => ({
    padding: '14px 16px', fontSize: 11, letterSpacing: 1, color: '#475569', fontWeight: 600,
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    borderBottom: '1px solid #eef2f6',
    background: sortKey === col ? '#fef2f2' : 'transparent',
    textAlign: 'left',
  });

  const PERIOD_LABELS = { thisMonth: 'This Month', lastMonth: 'Last Month', thisYear: 'This Year', lastYear: 'Last Year', '2years': '2 Years', '3years': '3 Years', '5years': '5 Years' };

  if (loading) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh', padding: 32 }}>
        <Skeleton height={36} width="30%" style={{ marginBottom: 8 }} />
        <Skeleton height={14} width="50%" style={{ marginBottom: 32 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
          <SkeletonChart /><SkeletonChart />
        </div>
        <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#fff', minHeight: '100vh', padding: 32, textAlign: 'center', paddingTop: 100 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Failed to load dashboard</div>
        <div style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>{error}</div>
        <button onClick={fetchData} style={{ background: '#7f1a2e', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', minHeight: '100vh', padding: 32, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        tr:hover td { background: #fef2f2 !important; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: '#7f1a2e', fontWeight: 600, marginBottom: 6 }}>FINANCIAL ANALYSIS</div>
        <h1 style={{ fontSize: 34, fontWeight: 700, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>CEO Dashboard</h1>
        <div style={{ fontSize: 13, color: '#64748b' }}>Real-time financial analytics & cash flow insights</div>
      </div>

      {/* Time Period Selector */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24, padding: 14, background: '#f8fafc', borderRadius: 16 }}>
        {Object.entries(PERIOD_LABELS).map(([period, label]) => (
          <button key={period} onClick={() => { setTimePeriod(period); setPage(0); }} style={{
            padding: '7px 16px', borderRadius: 30, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 500,
            background: timePeriod === period ? '#7f1a2e' : '#fff',
            color: timePeriod === period ? '#fff' : '#475569',
            boxShadow: timePeriod === period ? '0 2px 8px rgba(127,26,46,0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
          }}>{label}</button>
        ))}
      </div>

      {/* KPI Cards - Including Cash Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 18, marginBottom: 28 }}>
        {[
          { label: 'Total Revenue', value: `${fmtM(summary.revenue)} UGX`, color: '#7f1a2e' },
          { label: 'Gross Profit', value: `${fmtM(summary.profit)} UGX`, color: '#10b981' },
          { label: 'Avg Profit Margin', value: pct(summary.margin), color: marginColor(summary.margin) },
          { label: 'Active Products', value: String(products.length), color: '#0f172a' },
          { label: 'Cash Inflow', value: `${fmtM(dashboardStats.cashSummary.totalInflow)} UGX`, color: '#10b981' },
          { label: 'Cash Outflow', value: `${fmtM(dashboardStats.cashSummary.totalOutflow)} UGX`, color: '#ef4444' },
          { label: 'Net Cash Flow', value: `${fmtM(dashboardStats.cashSummary.netCash)} UGX`, color: dashboardStats.cashSummary.netCash >= 0 ? '#10b981' : '#ef4444' },
          { label: 'Cash Balance', value: `${fmtM(dashboardStats.cashSummary.cashBalance)} UGX`, color: '#3b82f6' },
        ].map(k => (
          <div key={k.label} style={{ background: '#fff', border: '1px solid #eef2f6', borderRadius: 18, padding: '18px 22px' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Cash Flow Section - NEW! */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '32px 0 20px' }}>💰 Cash Flow Analysis</h2>
      
      {/* Cash Flow Chart */}
      <div style={{ marginBottom: 28 }}>
        <ChartContainer title="Monthly Cash Flow" subtitle="Cash inflows vs outflows" filename="cash_flow">
          {dashboardStats.cashFlowData && dashboardStats.cashFlowData.length > 0 ? (
            <SimpleCashFlowChart data={dashboardStats.cashFlowData} />
          ) : (
            <EmptyState title="No cash flow data" subtitle="Add payments and expenses to see cash flow" />
          )}
        </ChartContainer>
      </div>

      {/* Cash Flow Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginBottom: 32 }}>
        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: 20, padding: 24, color: '#fff' }}>
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8 }}>Total Cash Inflow</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>{fmtM(dashboardStats.cashSummary.totalInflow)} UGX</div>
          <div style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}>From payments received</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', borderRadius: 20, padding: 24, color: '#fff' }}>
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8 }}>Total Cash Outflow</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>{fmtM(dashboardStats.cashSummary.totalOutflow)} UGX</div>
          <div style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}>From expenses and COGS</div>
        </div>
        <div style={{ background: dashboardStats.cashSummary.netCash >= 0 ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', borderRadius: 20, padding: 24, color: '#fff' }}>
          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 8 }}>Net Cash Flow</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>{fmtM(dashboardStats.cashSummary.netCash)} UGX</div>
          <div style={{ fontSize: 12, marginTop: 8, opacity: 0.8 }}>{dashboardStats.cashSummary.netCash >= 0 ? 'Positive cash flow' : 'Negative cash flow'}</div>
        </div>
      </div>

      {/* Trend Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 24, marginBottom: 28 }}>
        <ChartContainer title="Revenue Trend" subtitle="Monthly revenue performance" filename="revenue_trend">
          <RevenueTrendCurve data={dashboardStats.revenueTrend} />
        </ChartContainer>
        <ChartContainer title="Revenue vs Profit" subtitle="Dual-axis performance comparison" filename="revenue_vs_profit">
          <DualAxisChart revenueData={dashboardStats.revenueTrend.map(d => ({ period: d.period, value: d.revenue }))} profitData={dashboardStats.profitTrend} />
        </ChartContainer>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 24, marginBottom: 28 }}>
        <ChartContainer title="Profit Margin Trend" subtitle="Margin with strategic threshold zones" filename="margin_trend">
          <MarginTrendChart data={dashboardStats.marginTrend} />
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 10, fontSize: 10, flexWrap: 'wrap' }}>
            {[['#10b981', 'Excellent ≥50%'], ['#eab308', 'Good 35-50%'], ['#f97316', 'Fair 20-35%'], ['#ef4444', 'Critical <20%']].map(([c, l]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, background: c, borderRadius: 2 }} />{l}</div>
            ))}
          </div>
        </ChartContainer>
        <ChartContainer title="Margin Health Distribution" subtitle="Products by margin tier" filename="margin_distribution">
          <SimplePieChart data={dashboardStats.marginDistribution} />
        </ChartContainer>
      </div>

      {/* BAR GRAPHS SECTION */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '32px 0 20px' }}>📊 Bar Chart Analysis</h2>
      
      {/* Row 1: Monthly Revenue vs Profit - Grouped Bar */}
      <div style={{ marginBottom: 28 }}>
        <ChartContainer title="Monthly Revenue vs Profit" subtitle="Grouped bar comparison by month" filename="monthly_revenue_profit">
          {dashboardStats.monthlyRevenueProfit && dashboardStats.monthlyRevenueProfit.length > 0 ? (
            <SimpleGroupedBar 
              data={dashboardStats.monthlyRevenueProfit.map(m => ({ label: m.label, value1: m.revenue, value2: m.profit }))}
              colors={['#7f1a2e', '#10b981']}
            />
          ) : (
            <EmptyState title="No monthly data" subtitle="Add orders to see monthly trends" />
          )}
        </ChartContainer>
      </div>

      {/* Row 2: Category Revenue & Top Products Revenue - Horizontal Bars */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 24, marginBottom: 28 }}>
        <ChartContainer title="Revenue by Category" subtitle="Horizontal bar chart showing revenue distribution" filename="category_revenue">
          {dashboardStats.categoryRevenue && dashboardStats.categoryRevenue.length > 0 ? (
            <SimpleHorizontalBar data={dashboardStats.categoryRevenue} color="#7f1a2e" />
          ) : (
            <EmptyState title="No category data" subtitle="Add categories to products" />
          )}
        </ChartContainer>
        
        <ChartContainer title="Top Products by Revenue" subtitle="Highest revenue generating products" filename="top_products_revenue">
          {dashboardStats.topProductsRevenue && dashboardStats.topProductsRevenue.length > 0 ? (
            <SimpleHorizontalBar data={dashboardStats.topProductsRevenue.slice(0, 8).map(p => ({ label: p.name, value: p.revenue }))} color="#7f1a2e" />
          ) : (
            <EmptyState title="No product data" subtitle="Add products to see rankings" />
          )}
        </ChartContainer>
      </div>

      {/* Row 3: Quarterly Performance & Top Margin Products */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 24, marginBottom: 28 }}>
        <ChartContainer title="Quarterly Performance" subtitle="Revenue trends by quarter" filename="quarterly_performance">
          {dashboardStats.quarterlyPerformance && dashboardStats.quarterlyPerformance.length > 0 ? (
            <SimpleVerticalBar data={dashboardStats.quarterlyPerformance.map(q => ({ label: q.label, value: q.revenue }))} color="#7f1a2e" />
          ) : (
            <EmptyState title="No quarterly data" subtitle="Add orders to see quarterly trends" />
          )}
        </ChartContainer>
        
        <ChartContainer title="Top Products by Margin" subtitle="Highest margin products" filename="top_products_margin">
          {dashboardStats.topProductsMargin && dashboardStats.topProductsMargin.length > 0 ? (
            <SimpleHorizontalBar data={dashboardStats.topProductsMargin.slice(0, 8).map(p => ({ label: p.name, value: p.margin }))} color="#10b981" />
          ) : (
            <EmptyState title="No product data" subtitle="Add products to see margin rankings" />
          )}
        </ChartContainer>
      </div>

      {/* Row 4: Stacked Bar Chart - Revenue Stacked by Quarter */}
      <div style={{ marginBottom: 28 }}>
        <ChartContainer title="Revenue Composition by Quarter" subtitle="Stacked bar chart showing revenue and profit contribution" filename="stacked_quarterly">
          {dashboardStats.quarterlyPerformance && dashboardStats.quarterlyPerformance.length > 0 ? (
            <SimpleGroupedBar 
              data={dashboardStats.quarterlyPerformance.map(q => ({ label: q.label, value1: q.revenue, value2: q.profit }))}
              colors={['#7f1a2e', '#10b981']}
            />
          ) : (
            <EmptyState title="No quarterly data" subtitle="Add orders to see stacked view" />
          )}
        </ChartContainer>
      </div>

      {/* Product Table */}
      <div style={{ marginBottom: 16, marginTop: 32 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' }}>Product Profitability Table</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="🔍 Search products..."
            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '9px 14px', fontSize: 13, width: 260, outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => { setSelectedCategory(cat); setPage(0); }} style={{
                padding: '7px 16px', borderRadius: 30, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 500,
                background: selectedCategory === cat ? '#7f1a2e' : '#f1f5f9',
                color: selectedCategory === cat ? '#fff' : '#475569',
              }}>{cat}</button>
            ))}
          </div>
          <button onClick={() => { setShowLowMarginOnly(v => !v); setPage(0); }} style={{
            padding: '7px 18px', borderRadius: 30, cursor: 'pointer', fontSize: 12,
            border: `1px solid ${showLowMarginOnly ? '#ef4444' : '#e2e8f0'}`,
            background: showLowMarginOnly ? '#fef2f2' : '#fff',
            color: showLowMarginOnly ? '#ef4444' : '#475569',
          }}>⚠️ Low Margin Only</button>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>{filtered.length} products</div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #eef2f6', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafcff' }}>
                {[
                  { key: 'name', label: 'PRODUCT' },
                  { key: 'revenue', label: 'REVENUE' },
                  { key: 'profit', label: 'GROSS PROFIT' },
                  { key: 'margin', label: 'MARGIN %' },
                  { key: 'profitPerUnit', label: 'PROFIT/UNIT' },
                  { key: 'quantity', label: 'UNITS' },
                ].map(col => (
                  <th key={col.key} style={thStyle(col.key)} onClick={() => handleSort(col.key)}>
                    {col.label}<SortIcon col={col.key} />
                  </th>
                ))}
                <th style={{ padding: '14px 16px', fontSize: 11, color: '#475569', fontWeight: 600, borderBottom: '1px solid #eef2f6' }}>TREND</th>
                <th style={{ padding: '14px 16px', fontSize: 11, color: '#475569', fontWeight: 600, borderBottom: '1px solid #eef2f6' }}>HEALTH</th>
               </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 0 }}><EmptyState title="No products match" subtitle="Try adjusting your filters" /></td></tr>
              )}
              {paginatedRows.map((p, i) => {
                const mColor = marginColor(p.margin);
                return (
                  <tr key={p.id} onClick={() => setSelectedProduct(p)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafcff' }}>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 3 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{p.category}</div>
                      <MiniBar value={p.revenue} max={maxRevenue} color={mColor} />
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: 600, color: '#7f1a2e' }}>{fmtM(p.revenue)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{fmtM(p.profit)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                      <span style={{ background: `${mColor}18`, color: mColor, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{pct(p.margin)}</span>
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', color: '#a78bfa' }}>{fmtM(p.profitPerUnit)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', color: '#64748b' }}>{fmt(p.quantity)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'center' }}><Sparkline data={p.sparkData} width={100} height={36} /></td>
                    <td style={{ padding: '13px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: mColor, background: `${mColor}18`, padding: '3px 10px', borderRadius: 20 }}>
                        {marginLabel(p.margin)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination total={filtered.length} page={page} setPage={setPage} />
        <div style={{ padding: '12px 20px', borderTop: '1px solid #eef2f6', display: 'flex', gap: 20, fontSize: 11, color: '#64748b' }}>
          <span>🟢 Excellent ≥50%</span>
          <span>🟡 Good 35-50%</span>
          <span>🟠 Fair 20-35%</span>
          <span>🔴 Critical &lt;20%</span>
          <span style={{ marginLeft: 'auto' }}>Click any row for detailed analysis →</span>
        </div>
      </div>

      <TrendModal product={selectedProduct} onClose={() => setSelectedProduct(null)} totalProducts={products.length} />
    </div>
  );
};

export default CeoDashboard;